import { useId } from "react";
import type { LogotypeWordmark } from "@school-sales-ai/shared";
import { cn } from "@/lib/utils";

/**
 * Loxago's logotype, drawn from the brand's own artwork
 * (`public/brand/loxago/logo-loxago.svg`) — the letters are geometry, not a font,
 * so no amount of Space Grotesk reproduces them.
 *
 * Sized in `em` so it drops into every slot that sizes the text wordmark with a
 * `text-[22px]`-style class: the caller's font size scales it and no caller has
 * to know which kind of wordmark it is holding.
 *
 * The gradient id comes from `useId()` because the sidebar and a login card can
 * both be in one DOM (tests, a future embed), and two `<linearGradient>`s sharing
 * an id make the second logotype paint with the first one's stops.
 */
type LoxagoLogotypeProps = {
  wordmark: LogotypeWordmark;
  ground: "light" | "dark";
  /** Accessible name — the brand's prose name. */
  label: string;
  className?: string;
};

export function LoxagoLogotype({
  wordmark,
  ground,
  label,
  className,
}: LoxagoLogotypeProps): React.JSX.Element {
  const gradientId = `loxago-${useId()}`;
  const [from, to] = ground === "dark" ? wordmark.gradient.onDark : wordmark.gradient.onLight;
  const paint = `url(#${gradientId})`;

  return (
    <svg
      viewBox="0 0 1253 207"
      role="img"
      aria-label={label}
      className={cn("inline-block h-[0.8em] w-auto align-baseline", className)}
    >
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="158" y1="0" x2="1411" y2="0">
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </linearGradient>
      </defs>
      <g transform="translate(-158 -261)">
        <polygon fill={paint} points="158,263 173,263 173,453 291,453 291,466 158,466" />
        <ellipse fill="none" stroke={paint} strokeWidth="14.5" cx="421" cy="364.5" rx="98.75" ry="96" />
        <polygon fill={paint} points="561,263 578,263 729,466 711,466" />
        <polygon fill={paint} points="707,263 724,263 572,466 555,466" />
        <polygon fill={paint} points="743,466 837,263 852,263 945,466 929,466 844.5,280 760,466" />
        <path
          fill="none"
          stroke={paint}
          strokeWidth="14.5"
          d="M1143,294.9 A98.75,96 0 1 0 1140.5,436.3 L1140.5,365"
        />
        <ellipse fill="none" stroke={paint} strokeWidth="14.5" cx="1305.5" cy="364.5" rx="98.75" ry="96" />
      </g>
    </svg>
  );
}
